import { IDLE_READINGS, IDLE_TUNING, PAIRED_RULES, REQUIRED_TIMINGS, THRESHOLDS, isPayRule } from "./constants"

type Section = Record<string, unknown>

export const isObject = (value: unknown): value is Section => (
  typeof value === "object" && value !== null && !Array.isArray(value)
)

const isPositive = (value: unknown): boolean => typeof value === "number" && Number.isFinite(value) && value > 0

const inRange = (value: unknown, min: number, max: number, minInclusive: boolean): boolean => (
  typeof value === "number"
  && Number.isFinite(value)
  && (minInclusive ? value >= min : value > min)
  && value <= max
)

const valueAt = (section: Section, path: string[], fallback?: number): unknown => {
  const value = path.reduce<unknown>((node, key) => (isObject(node) ? node[key] : undefined), section)

  return value === undefined ? fallback : value
}

const rangeText = (min: number, max: number, minInclusive: boolean) => `${minInclusive ? "from" : "above"} ${min} to ${max}`

export const vnRoomsProblems = (data: unknown): string[] => {
  if (!isObject(data)) return ["The config must be a JSON object"]

  const problems: string[] = []

  for (const field of REQUIRED_TIMINGS) {
    if (!isPositive(data[field])) problems.push(`${field} is missing or not above 0 (game-service rejects the whole config)`)
  }

  const listed = data.enabledPayRules
  if (listed !== undefined && !Array.isArray(listed)) {
    problems.push("enabledPayRules must be a list")

    return problems
  }
  const enabledPayRules = (listed ?? []) as unknown[]
  const unknownRules = enabledPayRules.filter(rule => !isPayRule(rule))
  if (unknownRules.length > 0) problems.push(`enabledPayRules names unknown rules: ${unknownRules.map(String).join(", ")}`)

  const enabled = new Set(enabledPayRules.filter(isPayRule))
  for (const [rule, needs] of PAIRED_RULES) {
    if (enabled.has(rule) && !enabled.has(needs)) problems.push(`${rule} needs ${needs}`)
  }

  for (const { path, neededBy, min, max, minInclusive, fallback } of THRESHOLDS) {
    const on = neededBy.filter(rule => enabled.has(rule))
    if (on.length === 0) continue
    if (!inRange(valueAt(data, path, fallback), min, max, minInclusive)) {
      problems.push(`${path.join(".")} must be ${rangeText(min, max, minInclusive)} while ${on.join(", ")} is on`)
    }
  }

  const idleReading = valueAt(data, ["idleReading"])
  if (idleReading !== undefined && !(IDLE_READINGS as readonly unknown[]).includes(idleReading)) {
    problems.push(`idleReading must be one of ${IDLE_READINGS.join(", ")} (video-processing drops every pay rule otherwise)`)
  }
  for (const { path, min, max, minInclusive, fallback } of IDLE_TUNING) {
    if (!inRange(valueAt(data, path, fallback), min, max, minInclusive)) {
      problems.push(`${path.join(".")} must be ${rangeText(min, max, minInclusive)} (video-processing drops every pay rule otherwise)`)
    }
  }

  return problems
}
